import styled, {keyframes} from 'styled-components';
import {mobileMedia, desktopMedia} from '../../../globalVariables/GlobalVariables';
import {dFlex, dFlexColumn, dFlexCenter, alignCenter} from '../../../globalVariables/GlobalFlex';

export const carousel = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    ${dFlexColumn}
    ${alignCenter}
    gap: var(--space-md);
`;

export const cardArea = styled.div`
    /* The card is laid out at its native size and scaled to fit (--card-scale, set in JS),
       so text + chrome shrink together. --card-w is the resulting on-screen card width. */
    --card-scale: 1;
    --card-w: calc(330px * var(--card-scale));
    position: relative;
    flex: 1;
    min-height: 0;
    width: 100%;
`;

export const viewport = styled.div`
    height: 100%;
    width: 100%;
    ${dFlex};
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    &::-webkit-scrollbar {
        display: none;
    }

    /* gap between the peeking cards + runway so the first/last card still snaps to centre */
    ${mobileMedia} {
        gap: var(--space-lg);
        padding-inline: calc((100% - var(--card-w)) / 2);
    }
`;

export const slide = styled.div`
    flex: 0 0 100%;
    height: 100%;
    ${dFlexCenter};
    scroll-snap-align: center;
    scroll-snap-stop: always;

    /* the peeking (non-active) cards are faded; the centred one is solid */
    opacity: 0.45;
    transition: opacity 0.3s ease;

    &.is-active {
        opacity: 1;
    }

    /* slide hugs the card so the neighbouring slides peek at the edges */
    ${mobileMedia} {
        flex: 0 0 var(--card-w);
    }
`;

/* Reserves the on-screen (scaled) size; the native-size card inside is scaled into it (see cardScaler) */
export const cardFrame = styled.div`
    width: var(--card-w);
    height: calc(690px * var(--card-scale));
    overflow: hidden;
    ${dFlex};
    justify-content: center;
    align-items: flex-start;
`;

const popIn = keyframes`
    from { opacity: 0; transform: translate(8px, -50%) scale(0.9); }
    to { opacity: 1; transform: translate(0, -50%) scale(1); }
`;

const swipeSweep = keyframes`
    0%, 14% { transform: translateX(13px) rotate(11deg); }
    54%, 70% { transform: translateX(-15px) rotate(-11deg); }
    100% { transform: translateX(13px) rotate(11deg); }
`;

export const swipeHint = styled.div`
    position: fixed;
    inset-inline-end: var(--space-md);
    top: 50%;
    transform: translateY(-50%);
    z-index: 40;
    pointer-events: none;
    ${dFlexColumn}
    ${alignCenter}
    gap: var(--space-sm);
    padding: var(--space-md) var(--space-lg);
    border-radius: var(--border-radius-border-radius-xl);
    color: var(--color-border-default);
    font-size: var(--font-size-body-small);
    font-weight: var(--font-weight-regular);
    white-space: nowrap;
    background: rgba(28, 28, 30, 0.55);
    border: 1px solid var(--color-bg-surface-hover);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
    -webkit-backdrop-filter: blur(14px) saturate(1.4);
    backdrop-filter: blur(14px) saturate(1.4);
    animation: ${popIn} 0.5s cubic-bezier(0.34, 1.6, 0.64, 1) backwards;
    transition: opacity 0.3s ease;

    & svg {
        width: 32px;
        height: 32px;
        transform-origin: 60% 80%;
        animation: ${swipeSweep} 2s ease-in-out 0.4s infinite;
    }

    &.is-hidden {
        opacity: 0;
    }

    /* mobile-only — on desktop the side arrows handle navigation */
    ${desktopMedia} {
        display: none;
    }

    @media (prefers-reduced-motion: reduce) {
        & svg {
            animation: none;
        }
    }
`;

export const dots = styled.div`
    ${dFlex}
    ${alignCenter}
    gap: var(--space-sm);
`;

export const dot = styled.button`
    width: 6px;
    height: 6px;
    padding: 0;
    border: none;
    cursor: pointer;
    border-radius: var(--border-radius-border-radius-full);
    background: var(--color-bg-surface-hover);
    transition: width 0.25s ease, background 0.25s ease;

    &.is-active {
        width: 18px;
        background: var(--color-border-default);
    }
`;

export const navButton = styled.button`
    width: 52px;
    height: 52px;
    flex: none;
    border: none;
    cursor: pointer;
    color: var(--color-border-default);
    background: var(--color-bg-surface-default);
    border-radius: var(--border-radius-border-radius-full);
    ${dFlexCenter};
    transition: background 0.15s ease, opacity 0.15s ease;

    & svg {
        display: block;
        width: 16px;
        height: 26px;
        transform-origin: center;
    }

    &:hover:not(:disabled) {
        background: var(--color-bg-surface-hover);
    }

    &:disabled {
        opacity: 0.35;
        cursor: default;
    }

    /* narrow screens swipe instead — arrows live only on desktop, flanking the card's sides */
    ${mobileMedia} {
        display: none;
    }

    ${desktopMedia} {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        z-index: 10;
        --arrow-offset: calc(50% + var(--card-w) / 2 + var(--space-lg));

        &.prev {
            inset-inline-end: var(--arrow-offset);
        }
        &.next {
            inset-inline-start: var(--arrow-offset);
        }
    }

    &.prev svg {
        transform: rotate(0deg);
    }
    &.next svg {
        transform: rotate(180deg);
    }
    &.prev:dir(rtl) svg {
        transform: rotate(180deg);
    }
    &.next:dir(rtl) svg {
        transform: rotate(0deg);
    }
`;
